import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button, Dialog, NavBar, Skeleton, Toast} from 'antd-mobile';
import {getNavBarContainerStyle, getBottomNavBarContainerStyle} from "../../../utils/deviceUtils";

import PhotoViewer from '../components/PhotoViewer';
import MultiPhotoSelectorView from '../components/MultiPhotoSelectorView';
import MultiPhotoGalleryView from '../components/MultiPhotoGalleryView';
import InfoItem from '../components/InfoItem';
import {PhotoType, PositionType} from '../services/PhotoService';
import multiPhotoService from '../services/MultiPhotoService';
import {NativePermissions} from '../components/PhotoNativeBridge';
import DeliveryDataService, {
    FailedDeliveryOrder, FILE_UPLOAD_FAILED_KEY,
} from "../../../services/DeliveryDataService";
import {PhotoProcessingUtils} from "../utils/PhotoProcessingUtils";
import EmptyState from "../../../components/EmptyState";
import {timestampToDate} from "../../../utils/dateTimeUtils";
import {useDeliveryTaskOrder, useDeliveryTaskOrderOperations, useFromMapMode, useTempScanPhotos} from '../store/deliveryStore';
import {ExceptionDictionaryType, ExceptionSubReasonType} from "../graphql/queries/getExceptionDictionaryForDelivery";
import {useLocationStore} from "../../../store/locationStore";
import {DeliveringPath} from "../../../enums/DeliveryNavigatePathEnum";
import {t} from "../../../i18n";
import {useTranslation} from "react-i18next";
import {getExceptionTypeText, getLocalizedText} from '../../../utils/i18nUtils';
import {formatStopNumberFromDeliveryTaskOrder} from '../../../utils/stopNumberUtils';
import OrderNoteItem from '../../../components/OrderNoteItem/OrderNoteItem';
import {openNativeMapMode} from 'utils/appRuntime';
import geolocationService, {GeoPosition} from "../services/GeolocationService";
import {openCamera} from "../../../utils/appRuntime";
import {maskPhoneNumber} from "../../../utils/phoneUtils";

interface MarkExceptionProps {
}

type ScanResultItem = {
    trackingNumber: string;
    stopNumber?: string;
    frameDataUrl?: string;
    deliveryTaskOrderId?: number;
};

// 最大照片数量
const kMaxSelectorPhotoLength = 6;

// 根据异常原因获取拍照配置
const getExceptionPhotoConfig = (exceptionReasonCode: string) => {
    // 根据异常原因代码或中文描述匹配
    const code = exceptionReasonCode || '';

    // 地址异常
    if (code === "ERDL001") {
        return {
            minPhotos: 2,
            requirementText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.addressException.detailPagePrompt'),
            cameraText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.addressException.cameraPagePrompt')
        };
    }
    // 无法进入派件区域
    if (code === "ERDL002") {
        return {
            minPhotos: 2,
            requirementText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.noAccess.detailPagePrompt'),
            cameraText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.noAccess.cameraPagePrompt')
        };
    }
    // 商户未营业
    if (code === "ERDL003") {
        return {
            minPhotos: 3,
            requirementText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.notInBusiness.detailPagePrompt'),
            cameraText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.notInBusiness.cameraPagePrompt')
        };
    }
    // 收件人拒收
    if (code === "ERDL004") {
        return {
            minPhotos: 2,
            requirementText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.refusedFreight.detailPagePrompt'),
            cameraText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.refusedFreight.cameraPagePrompt')
        };
    }

    // 地址无法投递
    if (code === "ERDL006") {
        return {
            minPhotos: 2,
            requirementText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.restrictedAddress.detailPagePrompt'),
            cameraText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.restrictedAddress.cameraPagePrompt')
        };
    }

    // 默认配置
    return {
        minPhotos: 2,
        requirementText: '请至少拍摄2张照片',
        cameraText: '请拍摄照片'
    };
};

const MarkExceptionTemporaryPage: React.FC<MarkExceptionProps> = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const {i18n} = useTranslation();
    const [photos, setPhotos] = useState<Record<PositionType, PhotoType | undefined>>({} as Record<PositionType, PhotoType | undefined>);
    const [photosArray, setPhotosArray] = useState<PhotoType[]>([]);
    const [showMultiPhotoSelector, setShowMultiPhotoSelector] = useState<boolean>(true); // 异常页面默认使用新版本连拍
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const {position} = useLocationStore();
    const deliveryTaskOrderId = location.state?.deliveryTaskOrderId;
    const trackingNumber = location.state?.trackingNumber || '';
    const exceptionReason: ExceptionDictionaryType = location.state?.exceptionReason;
    const exceptionSubReasons: ExceptionSubReasonType[] = location.state?.exceptionSubReasons ?? (location.state?.exceptionSubReason ? [location.state.exceptionSubReason] : []);
    const scanResults: ScanResultItem[] = location.state?.scanResults || [];
    const activeTab = location.state?.activeTab; // 获取来源页面的 activeTab
    const {deliveryTaskOrder, loading} = useDeliveryTaskOrder(deliveryTaskOrderId);
    const {handleDeliveryFailed} = useDeliveryTaskOrderOperations();
    const {fromMapMode, setFromMapMode} = useFromMapMode();
    const {getTempScanPhotos, clearTempScanPhotos} = useTempScanPhotos();
    const [hasAutoOpenedCamera, setHasAutoOpenedCamera] = useState<boolean>(false); // 标记是否已自动打开相机


    // 检查是否来自地图模式（Web地图）
    const isFromMapMode = fromMapMode || location.state?.fromMapMode;

    // 获取异常原因的拍照配置
    const photoConfig = getExceptionPhotoConfig(exceptionReason?.exceptionReasonCode || '');

    sessionStorage.setItem('returningFromDetail', 'true');

    // 图片只从 Store 获取并初始化 photosArray；退出页面或组件卸载时清空
    useEffect(() => {
        const storePhotos = getTempScanPhotos();
        const initialPhotos: PhotoType[] = [];
        if (trackingNumber && storePhotos.has(trackingNumber)) {
            const base64 = storePhotos.get(trackingNumber)!;
            initialPhotos.push({
                url: base64,
                objectKey: FILE_UPLOAD_FAILED_KEY,
                latitude: 0,
                longitude: 0,
                capturedAt: Date.now(),
                source: 'camera' as const
            });
        }
        if (initialPhotos.length > 0) {
            setPhotosArray(initialPhotos);
        }
        return () => {
            clearTempScanPhotos();
        };
    }, [trackingNumber]);

    useEffect(() => {
        // 只在首次进入且还未自动打开相机时执行
        if (!hasAutoOpenedCamera) {
            // 延迟执行，确保 photosArray 已经从 scanResults 加载完成
            const timer = setTimeout(() => {
                handleMultiPhotoGalleryBurstCapture();
                setHasAutoOpenedCamera(true); // 标记已打开
            }, 300);

            return () => clearTimeout(timer);
        }
    }, [photosArray, hasAutoOpenedCamera]); // 依赖于 photosArray 的变化
    console.log("photosArray",photosArray)

    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await NativePermissions.request('camera');
                await NativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    // 保存订单数据到本地
    const saveOrderToLocal = async (
        customPhotosArray?: PhotoType[],
        customCompletionLocation?: GeoPosition | null
    ): Promise<boolean> => {
        try {
            // 使用传入的参数或当前状态
            const currentPhotosArray = customPhotosArray || photosArray;

            const order: FailedDeliveryOrder = {
                trackingNumber: trackingNumber,
                updatedAt: Date.now(),
                photos: exceptionReason.isUploadsImages
                    ? PhotoProcessingUtils.processPhotosForSaving(currentPhotosArray, photos, showMultiPhotoSelector)
                    : [],
                location: position,
                stopNumber: deliveryTaskOrder?.stopNumber?.toString() || '0',
                subStopNumber: deliveryTaskOrder?.subStopNumber,
                id: deliveryTaskOrderId,
                exceptionReasonCode: exceptionReason.exceptionReasonCode,
                type: 'failed',
                completionLocation: customCompletionLocation,
                ...(exceptionSubReasons.length > 0 ? { exceptionSubReasons: exceptionSubReasons.map(s => s.code) } : {})
            };

            // 保存到本地
            const saved = await DeliveryDataService.saveDeliveryOrder(order);
            return saved;
        } catch (error) {
            console.error('Mark Exception to save order to local:', error);
            return false;
        }
    };

    /*照片相关操作 start======*/

    // 删除照片 - 新版本（数组）
    const deletePhotoArray = (index: number) => {
        Dialog.confirm({
            content: t('pages.delivery.exception.deletePhotoTitle'),
            confirmText: t('pages.delivery.exception.deletePhotoDelete'),
            cancelText: t('pages.delivery.exception.deletePhotoCancel'),
            onConfirm: () => {
                multiPhotoService.handleDeletePhotoArray(index, (updateFn) => {
                    setPhotosArray((prev) => updateFn(prev));
                });
            },
        });
    };

    // 处理多图画廊的连拍功能（新版本数组形式）
    const handleMultiPhotoGalleryBurstCapture = () => {
        // 计算剩余可拍照片数量
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);

        // 动态计算最小选择数量：如果当前照片数已经超过最小要求，则最小选择为0
        const minSelectionCount = currentPhotoCount >= photoConfig.minPhotos ? 0 : photoConfig.minPhotos - currentPhotoCount;

        openCamera(remainingPhotoCount, minSelectionCount, photoConfig.cameraText, async (photos: string[]) => {
            try {
                // ✅ 连拍时为每张照片获取实时位置（每张照片一个经纬度）
                const photosWithPosition: Array<{ photo: string; position: GeoPosition | null }> = [];

                for (const photo of photos) {
                    let currentPosition: GeoPosition | null = {
                        latitude: 0,
                        longitude: 0,
                        accuracy: 0,
                        timestamp: 0,
                    };
                    photosWithPosition.push({photo, position: currentPosition});
                }

                await multiPhotoService.handleMultiPhotoSelectionArrayWithIndividualPositions(
                    photosWithPosition,
                    (updateFn) => setPhotosArray(updateFn),
                    'camera' // 连拍来源：拍照
                );
            } catch (error) {
                console.error('多图片处理失败:', error);
                Toast.show({content: '多图片处理失败', position: 'center'});
            }
        });
    };

    /*照片相关操作 end======*/

    // 标记异常
    const handleCompleteException = async () => {
        setIsLoading(true);

        // ✅ 获取完成时的实时经纬度（用于 location 字段，与照片GPS独立）
        let completionPosition = position; // 默认使用缓存位置

        try {
            // 新版本：检查数组照片数量
            if (exceptionReason.isUploadsImages && (photosArray.length < photoConfig.minPhotos || !photosArray.every(photo => photo.url))) {
                Toast.show({
                    content: t('pages.delivery.detail.takeAtLeastPhotos', {count: photoConfig.minPhotos}),
                    position: 'center'
                });
                setIsLoading(false);
                return;
            }

            // 获取完成时的实时经纬度（强制获取最新位置，不使用缓存）
            try {
                completionPosition = await geolocationService.getFreshPosition();
                console.log('📍 异常完成时获取到最新位置:', completionPosition);
            } catch (error) {
                console.warn('⚠️ 获取位置失败，使用缓存位置:', error);
            }

            const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;
            // 仅执行乐观提交逻辑，不进行网络请求
            await handleDeliveryFailed({
                trackingNumber: currentTrackingNumber,
                location: {
                    latitude: completionPosition?.latitude || 0.0,
                    longitude: completionPosition?.longitude || 0.0
                },
                exceptionReasonCode: exceptionReason.exceptionReasonCode,
                id: deliveryTaskOrderId,
                exceptionImageS3KeysJson: [], // ✅ 保留旧字段
                ...(exceptionSubReasons.length > 0 ? { exceptionSubReasons: exceptionSubReasons.map(s => s.code) } : {})
            }, true);

            await saveOrderToLocal(
                photosArray,
                completionPosition // 保存完成时的GPS
            );
            clearTempScanPhotos();

            // 根据导航来源决定返回路径
            if (isFromMapMode) {
                setFromMapMode(false); // 清除导航来源
                sessionStorage.setItem('returningFromDetail', 'true');
                navigate("/delivery/temporary/map-mode");
            } else {
                navigate("/delivery/delivering/temporary", {
                    state: {
                        activeTab: activeTab
                    }
                });
            }
        } catch (error: any) {
            Toast.show({content: t('pages.delivery.orderDetail.markFailed'), position: 'center'});

        } finally {
            setIsLoading(false);
        }
    };

    // 处理返回按钮点击
    const handleBack = () => {
        if (isFromMapMode) {
            // 返回地图模式页面
            setFromMapMode(false);
            sessionStorage.setItem('returningFromDetail', 'true');
            navigate("/delivery/temporary/map-mode");
        } else {
            // 返回派送列表页面，并恢复 activeTab
            navigate("/delivery/delivering/temporary", {
                state: {
                    activeTab: activeTab
                }
            });
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>
                        Failed
                    </NavBar>
                </div>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={5} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={1} animated/>
                <Skeleton style={{width: '100%', height: '30%'}} animated/>
            </div>
        );
    }

    if (!deliveryTaskOrder) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>
                        Failed
                    </NavBar>
                </div>
                <EmptyState/>
            </div>
        );
    }

    const address = `${deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine1 || ''} 
                            ${deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine2 || ''} 
                            ${deliveryTaskOrder.orderWithAddress?.deliveryAddress?.city || ''} 
                            ${deliveryTaskOrder.orderWithAddress?.deliveryAddress?.state || ''}
                            ${deliveryTaskOrder.orderWithAddress?.deliveryAddress?.zipcode || ''} US`.trim();
    const stopNumber = deliveryTaskOrder.stopNumber;
    const fullName = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.fullName;
    const phone = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.phoneNumber;
    const routeNumber = deliveryTaskOrder.routeName;
    const deliverAt = deliveryTaskOrder.orderWithAddress?.order?.deliverAt;
    const deliveryAttempts = deliveryTaskOrder.orderWithAddress?.order?.deliveryAttempts || 0;

    const multiPhotoSelectorView = (() => {
        // 计算剩余可拍照片数量
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);

        // 动态计算最小选择数量：如果当前照片数已经超过最小要求，则最小选择为0
        const minSelectionCount = currentPhotoCount >= photoConfig.minPhotos ? 0 : photoConfig.minPhotos - currentPhotoCount;
        const cameraText = photoConfig.cameraText;
        // 原生端：使用 MultiPhotoSelectorView
        return (
            <MultiPhotoSelectorView
                minPhotos={minSelectionCount}
                maxPhotos={remainingPhotoCount}
                onPhotoSelected={async (photos: string[]) => {
                    try {
                        let currentPosition: GeoPosition | null = {
                            latitude: 0,
                            longitude: 0,
                            accuracy: 0,
                            timestamp: 0,
                        };

                        const result = await multiPhotoService.handleMultiPhotoSelectionArray(
                            photos,
                            (updateFn) => setPhotosArray(updateFn),
                            currentPosition,
                            'gallery' // 相册选择来源：相册
                        );

                        if (!result.success) {
                            console.warn('部分图片上传失败:', result.errors);
                            // 可以显示提示信息
                        }
                    } catch (error) {
                        console.error('多图片处理失败:', error);
                        Toast.show({content: '多图片处理失败', position: 'center'});
                    }
                }}
                cameraText={cameraText}
            />
        );
    })()

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={handleBack}>
                {t('pages.delivery.orderDetail.failed')}</NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="mb-1 bg-white p-4">
                    <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.orderInformation')}</h2>
                    <div className="mb-1">
                        <InfoItem label={`${t('pages.delivery.detail.trackingNumber')}`} value={trackingNumber}/>
                        <InfoItem label={`${t('pages.delivery.detail.stopNo')}`}
                                  value={deliveryTaskOrder ? formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder) : '0'}/>
                        <InfoItem label={`${t('pages.delivery.detail.routeNo')}`} value={routeNumber}/>
                        <InfoItem label={`${t('pages.delivery.detail.assignedTime')}`}
                                  value={deliverAt ? timestampToDate(deliverAt).toLocaleString() : ''}/>
                        <InfoItem label={`${t('pages.delivery.detail.deliveryAttempt')}`}
                                  value={`${deliveryAttempts} out of 3`}/>
                        <OrderNoteItem
                            orderNote={deliveryTaskOrder?.orderNote}
                            currentLanguage={i18n.language}
                        />
                        <div className="flex justify-between py-2 border-gray-100">
                            <span
                                className="text-gray-600 text-sm">{`${t('pages.delivery.exception.filedReason')}`}:</span>
                            <span
                                className="text-sm font-medium text-right text-violet-600">
                                {exceptionSubReasons.length > 0
                                    ? exceptionSubReasons.map(s => getLocalizedText({ zh: s.textZh, en: s.textEn, es: s.textEs }, s.textEn)).join(', ')
                                    : getExceptionTypeText(exceptionReason)}
                            </span>
                        </div>
                    </div>
                    <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.customerInformation')}</h2>
                    <div>
                        <InfoItem label={`${t('pages.delivery.detail.name')}`} value={fullName || ''}/>
                        <InfoItem label={`${t('pages.delivery.detail.phoneNumber')}`}
                                  value={maskPhoneNumber(phone) || ''}/>
                        <InfoItem label={`${t('pages.delivery.detail.address')}`} value={address}/>
                    </div>
                </div>

                <div className="mb-2 bg-white p-4 border-t-1 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.photos')}</h2>
                    <p className="text-sm text-gray-600 mb-1">
                        {!showMultiPhotoSelector
                            ? t('pages.delivery.exception.photoDesc')
                            : photoConfig.requirementText
                        }
                    </p>

                    {/* 新版本：连拍功能 */}
                    {(
                        <>
                            {/* 多图画廊展示 */}
                            <div className="mt-4">
                                <MultiPhotoGalleryView
                                    photos={photosArray
                                        .filter((photo): photo is PhotoType => photo?.url !== undefined)
                                        .map(photo => photo.url)
                                    }
                                    onDelete={(index: number) => {
                                        deletePhotoArray(index);
                                    }}
                                    maxDisplayCount={kMaxSelectorPhotoLength}
                                    onNativeBurstCapture={handleMultiPhotoGalleryBurstCapture}
                                />
                            </div>
                            {/* 相册上传 */}
                            {photosArray.length < kMaxSelectorPhotoLength &&
                                <div className="mt-4">
                                    {multiPhotoSelectorView}
                                </div>}
                        </>
                    )}
                </div>

                {/* 查看大图 */}
                <PhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />
            </div>

            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <Button
                    block
                    color="primary"
                    loading={isLoading}
                    disabled={exceptionReason.isUploadsImages && (photosArray.length < photoConfig.minPhotos || isLoading)}
                    onClick={handleCompleteException}
                >
                    {t('pages.delivery.detail.complete')}
                </Button>
            </div>
        </div>
    );
};

export default MarkExceptionTemporaryPage;
